import { CallSplit } from '@mui/icons-material';
import { Stack, Tooltip } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CampaignChannels } from 'domains/campaigns/types';
import FormLayout from 'domains/core/components/FormLayout';
import Select, { getOptionValues, Value } from 'domains/core/components/Select';
import { CampaignStatus, FilterCategory, RecordType } from 'models/enums';
import ActivityFilter from 'domains/segments/pages/SegmentCreatePage/Filter/ActivityFilter';
import DemographicFilter from 'domains/segments/pages/SegmentCreatePage/Filter/DemographicFilter';
import EventFilter from 'domains/segments/pages/SegmentCreatePage/Filter/EventFilter';
import { isValidEventsFilter } from 'domains/segments/pages/SegmentCreatePage/utils';
import {
    SegmentFilterCategorySpecificField,
    SegmentFilterEngagementFields,
    SegmentFilterEventFields,
    SegmentFilterState,
} from 'domains/segments/types';
import { getDemographicFilters, getEngagementNameToValuesRef } from 'domains/segments/utils';
import useSegmentFilters from 'hooks/queries/useSegmentFilters';
import useSends from 'hooks/queries/useSends';
import { getFriendlyName } from 'utils';
import { CustomCampaignSteps } from '../../../CustomCampaign';
import { setSegment } from '../../../customCampaignActions';
import { CampaignStateTypes } from '../../../customCampaignReducer';
import useSendById from 'hooks/queries/useSendById';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useQueryParams from 'hooks/useQueryParams';
import DisabledTooltipContent from '../../../components/DisabledTooltipContent';

const allContactsRecordType = {
    label: RecordType.ALL_CONTACTS,
    value: RecordType.ALL_CONTACTS,
};

const initialFilterState: SegmentFilterState<SegmentFilterCategorySpecificField> = {
    id: 0,
    category: FilterCategory.CONTACT_ACTIVITY,
    name: '',
    operator: null,
    categorySpecificFields: {
        value: null,
    },
    autocompleteValue: null,
    autocompleteInputValue: '',
};

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setNextStep: (step: CustomCampaignSteps) => void;
};

const CustomCampaignSplitCriteriaStep = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setIsContinueEnabled,
    setNextStep,
}: Props) => {
    const activeSend = campaignState.sends[activeSendIndex];
    const isActiveSendEmail = activeSend?.channel === CampaignChannels.EMAIL;
    const initialCategory =
        isActiveSendEmail && (activeSend?.segments?.[0]?.category || FilterCategory.CONTACT_ACTIVITY);
    const [category, setCategory] = useState(initialCategory);
    const [filter, setFilter] = useState(initialFilterState);
    const [datetimeErrors, setDatetimeErrors] = useState({});
    const valuesRef = useRef('');

    const activeSendValueUsed = isActiveSendEmail && (activeSend as any)?.segments?.[0]?.categorySpecificFields?.value;
    const { data: sendByIdData } = useSendById(activeSendValueUsed as number, {
        enabled: Boolean(activeSendValueUsed),
    });
    const { editLiveCurationsFeature: hasEditLiveCurationsFeatureFlag } = useFlags();
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;
    const disableEditing = hasEditLiveCurationsFeatureFlag && status === CampaignStatus.PAUSED;

    // Update filter autocomplete values when send data is available
    useEffect(() => {
        if (activeSendValueUsed && sendByIdData?.name) {
            filter.autocompleteInputValue = sendByIdData.name;
            filter.autocompleteValue = { label: sendByIdData.name, value: activeSendValueUsed };
            setFilter(filter);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSendValueUsed, sendByIdData]);

    const filterCategories: Value[] = Object.values(FilterCategory).map((category) => ({
        label: category,
        value: category,
    }));

    const { data: segmentFilters } = useSegmentFilters({
        version: 2,
    });

    const { data: sends } = useSends({
        path: valuesRef.current,
        reactQueryOptions: { enabled: Boolean(valuesRef.current) },
    });

    useEffect(() => {
        // Find EMAIL send with segments (may be different from activeSendIndex)
        let segmentsToUse: any[] | undefined;

        if (isActiveSendEmail && (activeSend as any)?.segments?.length) {
            segmentsToUse = (activeSend as any).segments;
        } else {
            // Search all sends for EMAIL send with segments
            const sendWithSegments = campaignState.sends.find(
                (send) =>
                    send.channel === CampaignChannels.EMAIL && 'segments' in send && (send as any).segments?.length
            ) as any;
            segmentsToUse = sendWithSegments?.segments;
        }

        // @ts-ignore
        if (segmentsToUse?.length) {
            const firstSegment = segmentsToUse[0];
            setFilter(firstSegment);
            if (firstSegment?.category) {
                setCategory(firstSegment.category);
            }
        }

        setNextStep(CustomCampaignSteps.SELECT_PATH_ACTION);
        // The dependency array only contains `activeSendIndex` because we only want it to run
        // if the component rerenders when we're switching from one split to another in the nav.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSendIndex]);

    const engagementFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        // RecordTypes[0] represents engagement filters because there's a single item and its recordType value is "" from the API
        return segmentFilters.engagement.recordTypes[0].filters;
    }, [segmentFilters]);

    const engagementNameToValuesRef = useMemo(() => getEngagementNameToValuesRef(engagementFilters), [
        engagementFilters,
    ]);

    // Set valuesRef to fetch sends when loading draft data
    useEffect(() => {
        if (filter?.name && engagementNameToValuesRef[filter.name]) {
            valuesRef.current = engagementNameToValuesRef[filter.name];
        }
    }, [filter?.name, engagementNameToValuesRef]);

    useEffect(() => {
        // TODO: Replace with existing filter validation "isFilterValid" from SegmentCreatePage/utils.ts
        const isValidActivitySegment = filter?.category === FilterCategory.CONTACT_ACTIVITY && !!filter?.operator;
        const isValidEventSegment = filter?.category === FilterCategory.CONTACT_EVENTS && isValidEventsFilter(filter);

        const hasDatetimeError = Object.values(datetimeErrors).includes(true);
        const isValidDemographicSegment =
            filter?.category === FilterCategory.CONTACT_DETAILS &&
            !!filter?.operator &&
            (!!filter?.autocompleteInputValue?.length || !!filter?.autocompleteValue) &&
            !hasDatetimeError;

        setIsContinueEnabled(isValidActivitySegment || isValidDemographicSegment || isValidEventSegment);
    }, [filter, setIsContinueEnabled, datetimeErrors]);

    const eventFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        const titleCasedRecordTypeFilters = segmentFilters.contactEvent.recordTypes?.map(({ recordType, filters }) => ({
            filters,
            recordType: getFriendlyName(recordType),
        }));
        return titleCasedRecordTypeFilters;
    }, [segmentFilters]);

    const recordTypes = getOptionValues([allContactsRecordType]);
    const segmentRecordTypes = segmentFilters?.demographic?.recordTypes;
    const demographicFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        const result = getDemographicFilters({ hasAllContacts: true, recordTypes, segmentRecordTypes });

        return result;
    }, [segmentFilters, recordTypes, segmentRecordTypes]);

    const handleCategorySelection = (e: any) => {
        setCategory(e.target.value);
        setFilter({
            ...initialFilterState,
            category: e.target.value,
        });
    };

    const handleDatetimeError = (filterId: string, error: boolean) =>
        setDatetimeErrors({ ...datetimeErrors, [filterId]: error });

    const handleUpdateSegmentFilter = (
        _index: number,
        filter: SegmentFilterState<SegmentFilterCategorySpecificField>
    ) => {
        setFilter(filter);
        dispatchCampaignAction(setSegment(filter, activeSendIndex));
        valuesRef.current = engagementNameToValuesRef[filter.name];
    };

    return (
        <FormLayout
            header="select split criteria"
            stepName="conditional split"
            icon={<CallSplit />}
            guidingText="Create a split in your Curation based on audience behavior."
        >
            <Stack alignItems="flex-start" columnGap={2} direction="row" flexWrap="wrap" rowGap={2}>
                <Tooltip title={disableEditing ? <DisabledTooltipContent /> : ''}>
                    <span>
                        <Select
                            label="Filter type"
                            disabled={disableEditing}
                            sx={{ textTransform: 'capitalize', width: '12rem' }}
                            value={category}
                            values={filterCategories}
                            onChange={handleCategorySelection}
                        />
                    </span>
                </Tooltip>
                {category === FilterCategory.CONTACT_ACTIVITY && engagementFilters && (
                    <ActivityFilter
                        filter={filter as SegmentFilterState<SegmentFilterEngagementFields>}
                        index={0}
                        engagementFilters={engagementFilters}
                        sends={sends || []}
                        sx={{ autocomplete: { width: '300px' } }}
                        updateSegmentFilter={(_index, filter) => {
                            handleUpdateSegmentFilter(_index, filter);
                            valuesRef.current = engagementNameToValuesRef[filter.name];
                        }}
                        disabled={disableEditing}
                        disabledTooltipContent={<DisabledTooltipContent />}
                    />
                )}
                {category === FilterCategory.CONTACT_DETAILS && demographicFilters && (
                    <Tooltip title={disableEditing ? <DisabledTooltipContent /> : ''} arrow followCursor>
                        <Stack direction="row" sx={disableEditing ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
                            <DemographicFilter
                                filter={filter}
                                index={0}
                                filterOptions={demographicFilters}
                                handleDatetimeError={handleDatetimeError}
                                updateSegmentFilter={handleUpdateSegmentFilter}
                            />
                        </Stack>
                    </Tooltip>
                )}
                {category === FilterCategory.CONTACT_EVENTS && eventFilters && (
                    <Tooltip title={disableEditing ? <DisabledTooltipContent /> : ''} arrow followCursor>
                        <Stack direction="row" sx={disableEditing ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
                            <EventFilter
                                eventRecordTypeFilters={eventFilters}
                                filter={filter as SegmentFilterState<SegmentFilterEventFields>}
                                index={0}
                                handleDatetimeError={handleDatetimeError}
                                isResponsive
                                updateSegmentFilter={handleUpdateSegmentFilter}
                            />
                        </Stack>
                    </Tooltip>
                )}
            </Stack>
        </FormLayout>
    );
};

export default CustomCampaignSplitCriteriaStep;
